import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight, Check, CircleAlert } from 'lucide-react'
import { Alert, Button } from '@agion/ui'
import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout'
import { Field } from '@/components/common/Field'
import { authService } from '@/services/auth'
import { isInvalidResetToken, passwordResetErrorMessage } from '@/services/auth-errors'
import { newPasswordHint } from '@/services/password-hint'

// The emailed link is https://login.<env>/reset-password#token=<token>. The token
// travels in the fragment, which browsers never send to a server or in a Referer.
function readTokenFromFragment(): string {
  const fragment = window.location.hash.replace(/^#/, '')
  return new URLSearchParams(fragment).get('token')?.trim() ?? ''
}

type LinkProblem = 'missing' | 'expired'

/**
 * Choose a new password with the emailed reset link (agion/Agion#2375). The page
 * takes the token out of the address bar as soon as it has read it, so it does
 * not stay in the browser history.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation()
  const [token] = useState(readTokenFromFragment)
  const [linkProblem, setLinkProblem] = useState<LinkProblem | null>(() => (token ? null : 'missing'))
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDone, setIsDone] = useState(false)

  useEffect(() => {
    if (!window.location.hash) return
    const { pathname, search } = window.location
    window.history.replaceState(window.history.state, '', `${pathname}${search}`)
  }, [])

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    setError('')
  }
  const handleConfirmChange = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
    setError('')
  }

  const mismatch = confirmPassword !== '' && confirmPassword !== password
  const canSubmit = password !== '' && confirmPassword === password

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (!canSubmit || isSubmitting) return

    setIsSubmitting(true)
    try {
      await authService.resetPassword({ token, password })
      setIsDone(true)
    } catch (err) {
      if (isInvalidResetToken(err)) {
        setLinkProblem('expired')
      } else {
        setError(passwordResetErrorMessage(err, t, 'confirm'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isDone) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop" role="status">
          <div className="lp-badge lp-badge-success">
            <Check size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('passwordReset.doneTitle')}</h2>
          <p className="lp-state-msg">{t('passwordReset.doneMessage')}</p>
          <hr className="lp-state-divider" />
          <Button asChild className="lp-btn-primary w-full">
            <Link to="/login">
              {t('passwordReset.signIn')}
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </AuthSplitLayout>
    )
  }

  if (linkProblem) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop" role="alert">
          <div className="lp-badge lp-badge-brand">
            <CircleAlert size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('passwordReset.linkInvalidTitle')}</h2>
          <p className="lp-state-msg">
            {linkProblem === 'expired' ? t('passwordReset.linkExpired') : t('passwordReset.linkMissing')}
          </p>
          <hr className="lp-state-divider" />
          <Link to="/forgot-password" className="lp-back">
            {t('passwordReset.requestNewLink')}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </AuthSplitLayout>
    )
  }

  return (
    <AuthSplitLayout>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('passwordReset.newTitle')}</h2>
        <p className="lp-subheading">{t('passwordReset.newDescription')}</p>
      </div>

      <form className="lp-form lp-rise lp-rise-3" onSubmit={handleSubmit} noValidate>
        {error && <Alert variant="error">{error}</Alert>}

        <Field
          id="password"
          type={showPassword ? 'text' : 'password'}
          label={t('passwordReset.newPasswordLabel')}
          value={password}
          onChange={handlePasswordChange}
          hint={newPasswordHint(t)}
          required
          autoComplete="new-password"
          disabled={isSubmitting}
          labelAction={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="lp-field-action"
              aria-controls="password confirmPassword"
              aria-pressed={showPassword}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              {showPassword ? t('login.hidePassword') : t('login.showPassword')}
            </Button>
          }
        />

        <Field
          id="confirmPassword"
          type={showPassword ? 'text' : 'password'}
          label={t('passwordReset.confirmPasswordLabel')}
          value={confirmPassword}
          onChange={handleConfirmChange}
          error={mismatch ? t('passwordReset.passwordsDontMatch') : ''}
          required
          autoComplete="new-password"
          disabled={isSubmitting}
        />

        <Button
          type="submit"
          className="lp-btn-primary w-full"
          disabled={isSubmitting || !canSubmit}
          isLoading={isSubmitting}
        >
          {isSubmitting ? t('passwordReset.saving') : t('passwordReset.saveButton')}
          {!isSubmitting && <ArrowRight size={16} aria-hidden="true" />}
        </Button>
      </form>

      <p className="lp-secondary-cta">
        <Link to="/login" className="lp-back">
          <ArrowLeft size={16} aria-hidden="true" />
          {t('passwordReset.backToSignIn')}
        </Link>
      </p>
    </AuthSplitLayout>
  )
}

export default ResetPasswordPage
